import React, { useMemo, useCallback } from 'react';
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';
import { useRefetchWhenIsSelected } from '@jira/navigation-apps__routing-hooks/src/useRefetchWhenIsSelected.tsx';
import { MENU_ID_PLANS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useSubscribeAndUpdateFavorites } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/services/set-favorite/index.tsx';
import { useStableItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/use-stable-items/index.tsx';
import { useReorderableStarredItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/use-reorderable-starred-items.tsx';
import { usePlanReorderingIds } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-plans-services/src/use-plan-reordering-ids.tsx';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { NestedItemDropTarget } from '@atlassian/navigation-drag-and-drop/nested-item-drop-target';
import { expVal } from '@atlassian/jira-feature-experiments';
import { PlansEmptyContent } from '../empty-content/index.tsx';
import type {
	PlansList_navigationCustomisation$data,
	PlansList_navigationCustomisation$key,
} from './__generated__/PlansList_navigationCustomisation.graphql';
import QUERY, { type PlanListQuery } from './__generated__/PlanListQuery.graphql';
import type { PlansList$data, PlansList$key } from './__generated__/PlansList.graphql';
import { PlanMenuItem } from './plan-menu-item/PlanMenuItem.tsx';

const recentMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-plans.plans.common.plans-list.recent',

	defaultMessage: 'Recent',
	description: 'Message for recent heading in the plans menu items',
});

const starredMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-plans.plans.common.plans-list.starred',

	defaultMessage: 'Starred',
	description: 'Message for starred heading in the plans menu items',
});

type Props = {
	fragmentRef: PlansList$key;
	navigationCustomisationFragment?: PlansList_navigationCustomisation$key;
};

const MAX_RECENT_PLANS = 5;

/**
 * We intentionally request 15 starred and 20 recent plans.
 * If all 15 starred plans were recently visited, we still show 5 recent plans as well.
 */
export const PlansList = ({
	fragmentRef,
	navigationCustomisationFragment,
}: Props): React.JSX.Element => {
	const { isSelectedPath } = useSidebarNav4();
	const cloudId = useCloudId();

	const [data, refetch] = useRefetchableFragment<PlanListQuery, PlansList$key>(
		graphql`
			fragment PlansList on Query
			@refetchable(queryName: "PlanListQuery")
			@argumentDefinitions(currentURLPath: { type: URL }) {
				jira @required(action: LOG) {
					favouritePlans: favourites(
						cloudId: $cloudId
						filter: { type: PLAN, sort: { order: DESC } }
						first: 15
					) @required(action: LOG) {
						__id
						pageInfo {
							hasNextPage
						}
						edges {
							node @required(action: LOG) {
								... on JiraPlan {
									id @required(action: LOG)
									planId
									title
									...PlanMenuItem @dangerously_unaliased_fixme
								}
							}
						}
					}
					recentPlans: recentItems(
						cloudId: $cloudId
						currentURL: $currentURLPath
						types: [PLAN]
						first: 20
					) @required(action: LOG) {
						__id
						edges {
							node @required(action: LOG) {
								... on JiraPlan {
									id @required(action: LOG)
									planId
									...PlanMenuItem @dangerously_unaliased_fixme
								}
							}
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const navigationCustomisation = useFragment(
		graphql`
			fragment PlansList_navigationCustomisation on SettingsNavigationCustomisation {
				sidebar {
					nodes {
						# It is necessary to suppress the lint rule, because these ARE used by useReorderableStarredItems
						# eslint-disable-next-line @atlassian/relay/unused-fields
						menuId
						# eslint-disable-next-line @atlassian/relay/unused-fields
						visible
					}
				}
			}
		`,
		navigationCustomisationFragment,
	);

	useRefetchWhenIsSelected({ cloudId, isSelectedPath, QUERY, refetch, menuId: MENU_ID_PLANS });

	const refetchOnChange = useCallback(
		({ onComplete }: { onComplete: () => void }) => {
			refetch({ cloudId }, { fetchPolicy: 'store-and-network', onComplete });
		},
		[cloudId, refetch],
	);

	useSubscribeAndUpdateFavorites({
		entityTypename: 'JiraPlan',
		favouriteConnectionId: data?.jira.favouritePlans.__id || '',
		recentConnectionId: data?.jira.recentPlans.__id || '',
		refetch: refetchOnChange,
	});

	if (
		navigationCustomisation &&
		expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)
	) {
		return <PlansListInnerNew data={data} navigationCustomisation={navigationCustomisation} />;
	}

	return <PlansListInnerOld data={data} />;
};

type PlansListInnerOldProps = {
	data: PlansList$data;
};

const PlansListInnerOld = (props: PlansListInnerOldProps) => {
	const { data } = props;
	const { getStableItems } = useStableItems();
	const { formatMessage } = useIntl();
	const starred = formatMessage(starredMessage);
	const recent = formatMessage(recentMessage);

	const favorites = useMemo(() => {
		if (!data) return { idSet: new Set(), list: [], hasItems: false };

		const ids: string[] = [];
		const list: React.JSX.Element[] = [];

		data.jira.favouritePlans.edges?.forEach((edge) => {
			if (edge) {
				if (edge.node.id) {
					ids.push(edge.node.id);
				}
				list.push(
					<PlanMenuItem
						key={edge.node.planId}
						fragmentRef={edge.node}
						favouriteConnectionId={data.jira.favouritePlans.__id}
					/>,
				);
			}
		});

		return { idSet: new Set(ids), list, hasItems: Boolean(list.length) };
	}, [data]);

	const recentPlans = useMemo(() => {
		if (!data) return { list: [], hasItems: false };

		type RecentNode = NonNullable<NonNullable<typeof data.jira.recentPlans.edges>[number]>['node'];
		const nodes: RecentNode[] = [];

		data.jira.recentPlans.edges?.forEach((edge) => {
			if (edge && edge.node.id && !favorites.idSet.has(edge.node.id)) {
				nodes.push(edge.node);
			}
		});

		const limitedNodes = nodes.slice(0, MAX_RECENT_PLANS);
		const stableOrderedNodes = getStableItems({ nodes: limitedNodes, idKey: 'id' });

		const list = stableOrderedNodes.map((node) => (
			<PlanMenuItem
				key={node.planId}
				fragmentRef={node}
				favouriteConnectionId={data.jira.favouritePlans.__id}
			/>
		));

		return { list, hasItems: Boolean(list.length) };
	}, [data, favorites.idSet, getStableItems]);

	if (!favorites.hasItems && !recentPlans.hasItems) {
		return <PlansEmptyContent />;
	}

	return (
		<>
			{favorites.hasItems && (
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{starred}</MenuSectionHeading>
						<MenuList>{favorites.list}</MenuList>
					</MenuSection>
				</MenuListItem>
			)}
			{recentPlans.hasItems && (
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{recent}</MenuSectionHeading>
						<MenuList>{recentPlans.list}</MenuList>
					</MenuSection>
				</MenuListItem>
			)}
		</>
	);
};

type PlansListInnerNewProps = {
	data: PlansList$data;
	navigationCustomisation?: PlansList_navigationCustomisation$data | null;
};

const PlansListInnerNew = (props: PlansListInnerNewProps) => {
	const { data, navigationCustomisation } = props;
	const { formatMessage } = useIntl();
	const { getStableItems } = useStableItems();
	const starred = formatMessage(starredMessage);
	const recent = formatMessage(recentMessage);

	const { entityAri, ownerAri, group } = usePlanReorderingIds();

	const favouritePlans = data?.jira.favouritePlans;

	const planOrder = navigationCustomisation || null;

	type PlanEdge = NonNullable<NonNullable<NonNullable<typeof favouritePlans>['edges']>[number]>;
	type PlanNode = NonNullable<PlanEdge['node']>;

	const planNodes: PlanNode[] = useMemo(() => {
		const nodes: PlanNode[] = [];

		favouritePlans?.edges?.forEach((edge) => {
			if (edge != null && edge !== undefined && edge.node != null && edge.node !== undefined) {
				nodes.push(edge.node);
			}
		});

		return nodes;
	}, [favouritePlans?.edges]);

	const favorites = useReorderableStarredItems<PlanNode>({
		favourites: planNodes,
		lastOrder: planOrder?.sidebar?.nodes,
		getId: (item) => item.id || '',
		getLabel: (item) => item?.title?.toString() || '',
		getHasNextPage: () => Boolean(favouritePlans?.pageInfo?.hasNextPage),
		renderMenuItem: (edge, currentItems, currentOrder, item) => {
			return (
				<PlanMenuItem
					key={edge.id}
					fragmentRef={edge}
					favouriteConnectionId={data?.jira.favouritePlans.__id || ''}
					currentItems={currentItems}
					currentOrder={currentOrder}
					item={item}
				/>
			);
		},
		maxItems: 15,
	});

	const recentPlans = useMemo(() => {
		if (!data) return { list: [], hasItems: false };

		type RecentNode = NonNullable<NonNullable<typeof data.jira.recentPlans.edges>[number]>['node'];
		const nodes: RecentNode[] = [];

		data.jira.recentPlans.edges?.forEach((edge) => {
			if (edge && edge.node.id && !favorites.idSet.has(edge.node.id)) {
				nodes.push(edge.node);
			}
		});

		const limitedNodes = nodes.slice(0, MAX_RECENT_PLANS);
		const stableOrderedNodes = getStableItems({ nodes: limitedNodes, idKey: 'id' });

		const list = stableOrderedNodes.map((node) => (
			<PlanMenuItem
				key={node.planId}
				fragmentRef={node}
				favouriteConnectionId={data.jira.favouritePlans.__id}
			/>
		));

		return { list, hasItems: Boolean(list.length) };
	}, [data, favorites.idSet, getStableItems]);

	if (!favorites.hasItems && !recentPlans.hasItems) {
		return <PlansEmptyContent />;
	}

	return (
		<>
			{favorites.hasItems && (
				<NestedItemDropTarget
					currentItems={favorites.currentItems}
					currentOrder={favorites.currentOrder}
					entityAri={entityAri}
					group={group}
					ownerAri={ownerAri}
				>
					<MenuListItem>
						<MenuSection>
							<MenuSectionHeading>{starred}</MenuSectionHeading>
							<MenuList>{favorites.list}</MenuList>
						</MenuSection>
					</MenuListItem>
				</NestedItemDropTarget>
			)}
			{recentPlans.hasItems && (
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{recent}</MenuSectionHeading>
						<MenuList>{recentPlans.list}</MenuList>
					</MenuSection>
				</MenuListItem>
			)}
		</>
	);
};
